import colors from '@/styles/colors'
import { LoadingButton } from '@mui/lab'
import { Box, Button, ButtonProps, Chip, Select, styled, Typography } from '@mui/material'

interface RoundedFilledButtonProps extends ButtonProps {
  darkMode?: boolean
}

export const RoundedFilledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'darkMode',
})<RoundedFilledButtonProps>(({ darkMode = true }) => ({
  backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : '#6366f1',
  backdropFilter: darkMode ? 'blur(10px)' : 'none',
  border: darkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid transparent',
  color: darkMode ? 'white' : '#ffffff',
  fontSize: 14,
  fontWeight: '600',
  letterSpacing: '-0.01em',
  borderRadius: 12,
  alignItems: 'center',
  padding: '10px 20px',
  height: 44,
  textTransform: 'none',
  boxShadow: darkMode ? '0 2px 8px rgba(0, 0, 0, 0.15)' : '0 2px 8px rgba(99, 102, 241, 0.25)',
  transition: 'background-color 0.2s, border-color 0.2s, transform 0.2s, box-shadow 0.2s',
  '& .MuiButton-startIcon': {
    marginRight: '8px',
    '& svg': {
      width: 18,
      height: 18,
    },
  },
  '&:hover': {
    backgroundColor: darkMode ? 'rgba(99, 102, 241, 0.2)' : '#4f46e5',
    borderColor: darkMode ? 'rgba(99, 102, 241, 0.4)' : 'transparent',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 16px rgba(99, 102, 241, 0.35)',
  },
  '&:active': {
    transform: 'translateY(0) scale(0.95)',
    backgroundColor: darkMode ? 'rgba(99, 102, 241, 0.3)' : '#4338ca',
  },
  '&:disabled': {
    color: darkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.7)',
    backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(99, 102, 241, 0.4)',
    borderColor: 'transparent',
  },
  '&.Mui-focusVisible': {
    boxShadow: 'none',
    outline: 'none',
    backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : '#6366f1',
  },
  '&:focus': {
    outline: 'none',
  },
}))

export const RoundedActionButton = styled(Button)(({ theme, variant }) => ({
  backgroundColor: theme.palette.brand.light,
  color: 'white',
  fontSize: 13,
  fontWeight: '600',
  borderRadius: 50,
  alignItems: 'center',
  padding: '8px 16px',
  '&:hover': {
    backgroundColor: theme.palette.brand.light,
  },

  ...(variant === 'contained' && {
    backgroundColor: theme.palette.brand.light,
    color: 'white',
    borderColor: theme.palette.brand.light,
    border: `1px solid ${theme.palette.brand.light}`,
  }),
  ...(variant === 'outlined' && {
    backgroundColor: 'transparent',
    color: theme.palette.brand.light,
    borderColor: theme.palette.brand.light,
    border: `1px solid ${theme.palette.brand.light}`,
    '&:hover': {
      borderColor: 'transparent',
      backgroundColor: theme.palette.brand.light,
      color: 'white',
    },
  }),
  '&:disabled': {
    color: 'white',
    backgroundColor: 'lightGray',
    borderColor: 'transparent',
  },
}))

export const RoundedOutlineButton = styled(LoadingButton)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: theme.palette.brand.light,
  borderColor: theme.palette.brand.light,
  border: `1px solid ${theme.palette.brand.light}`,
  fontSize: 13,
  fontWeight: '600',
  borderRadius: 50,
  alignItems: 'center',
  padding: '10px 40px',
  '&[aria-selected = true]': {
    border: `1px solid ${colors.themePink}`,
    backgroundColor: colors.themePinkLight,
    color: colors.themePink,
  },
}))

export const RequestActionButton = styled(LoadingButton)<{ kind?: 'accept' | 'reject' }>(
  ({ kind }) => ({
    height: '44px',
    padding: '0 32px',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: 'none',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:active': {
      transform: 'translateY(0) scale(0.98)',
    },

    ...(kind === 'reject' && {
      background: '#f8fafc',
      color: '#ef4444',
      border: '2px solid #fee2e2',
      '&:hover': {
        background: '#fef2f2',
        borderColor: '#fecaca',
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(239, 68, 68, 0.15)',
      },
    }),

    ...(kind === 'accept' && {
      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      color: '#ffffff',
      border: 'none',
      boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
      '&:hover': {
        background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 20px rgba(16, 185, 129, 0.4)',
      },
    }),
  }),
)

export const FollowActionButton = styled(RoundedOutlineButton)(({ theme }) => ({
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
  letterSpacing: '-0.01em',
  border: '1px solid #e5e7eb',
  color: '#374151',
  backgroundColor: '#ffffff',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  borderColor: theme.palette.brand.light,
  fontSize: 14,
  fontWeight: '600',
  borderRadius: '12px',
  alignItems: 'center',
  padding: '8px 24px',

  '&.Mui-disabled': {
    borderColor: '#e5e7eb',
    color: '#9ca3af',
    backgroundColor: '#ffffff',
    transform: 'none',
    boxShadow: 'none',
  },

  '& .btn-default-text': { display: 'inline' },
  '& .btn-hover-text': { display: 'none' },

  // Hover when NOT selected
  '&:not([aria-selected="true"]):hover': {
    backgroundColor: colors.meshdomBlue,
    borderColor: colors.meshdomBlue,
    color: '#ffffff',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.15)',

    '& .btn-default-text': { display: 'none' },
    '& .btn-hover-text': { display: 'inline' },
  },

  // Selected state
  '&[aria-selected="true"]': {
    backgroundColor: colors.meshdomBlue,
    borderColor: colors.meshdomBlue,
    color: '#ffffff',

    // Hover when selected (unfollow state)
    '&:hover': {
      border: `1px solid ${colors.themePink}`,
      backgroundColor: colors.themePinkLight,
      color: colors.themePink,
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.15)',
      '& .btn-default-text': { display: 'none' },
      '& .btn-hover-text': { display: 'inline' },
    },
  },
}))

export const BentoCard = styled(Box)(({ theme }) => ({
  position: 'relative',
  borderRadius: '18px',
  overflow: 'hidden',
  cursor: 'pointer',
  backgroundColor: '#ffffff',
  border: '2px solid rgba(226, 232, 240, 0.8)',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  height: '100%',

  '&:hover': {
    transform: 'translateY(-4px)',
    borderColor: 'rgba(99, 102, 241, 0.3)',
    boxShadow: '0 12px 32px rgba(99, 102, 241, 0.15)',

    '& .bento-image': {
      transform: 'scale(1.08)',
    },

    '& .bento-info': {
      background: 'linear-gradient(180deg, transparent 0%, rgba(15, 23, 42, 0.85) 100%)',
    },
  },
}))

export const TopicTag = styled(Chip)<{ themeColor: string; size?: 'small' | 'medium' }>(
  ({ themeColor, size }) => ({
    padding: '10px 0px',
    fontSize: size == 'small' ? '13px' : '14px',
    fontWeight: 600,
    color: themeColor,
    backgroundColor: `${themeColor}14`, // 8% alpha
    border: '1px solid',
    borderColor: `${themeColor}26`, // 15% alpha,
  }),
)

export const FormSection = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  padding: '20px',
  borderRadius: '12px',
  border: '1px solid #f1f5f9',
  marginBottom: '16px',
}))

export const FormLabel = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 600,
  color: '#374151',
  marginBottom: '8px',
}))

export const StyledSelect = styled(Select)(({ theme }) => ({
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#e5e7eb',
    borderRadius: '8px',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#3b82f6',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#3b82f6',
    borderWidth: '2px',
  },
  '& .MuiSelect-select': {
    padding: '12px 14px',
    fontSize: '14px',
  },
}))
